import React, { PropsWithChildren, useContext } from 'react';
import PropTypes from 'prop-types';
import { isSSR } from '@front10/utils';
require('intersection-observer');

/**
 * Callback function provided to the IntersectionObserver
 * in order to lazy load images that appear in the viewport.
 * It relies on the `data-src` attribute of the images
 * to work.
 * @param {Array} entries - Items being observed
 * @param {Object} observer - Intersection Observer instance
 */

const imageIsLoaded = (imageId: string) => {
  if (!isSSR()) {
    if (!window.FR) {
      window.FR = {};
    }
    if (!window.FR.__imagesLoaded) {
      window.FR.__imagesLoaded = {};
    }
    if (window.FR.__imagesLoaded[imageId]) {
      return true;
    } else {
      return false;
    }
  }
};

const saveNewImageLoaded = (imageId: string) => {
  if (!isSSR()) {
    if (!window.FR) {
      window.FR = {};
    }
    if (!window.FR.__imagesLoaded) {
      window.FR.__imagesLoaded = {};
    }
    window.FR.__imagesLoaded[imageId] = true;
  }
};

function lazyLoadImage(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const hdLoaderImg = new Image();
      const target = entry.target as HTMLDivElement;
      hdLoaderImg.src = target.dataset.src || '';
      if (
        !imageIsLoaded(target.dataset.src || '') &&
        !imageIsLoaded(target.dataset.defaultImage || '')
      ) {
        target.style.background = 'unset';
        target.style.backgroundColor = 'lightgray';
        target.style.animation = 'pulse 1s infinite ease-in-out';
      }
      hdLoaderImg.onload = () => {
        // eslint-disable-next-line no-param-reassign
        target.style.background = `url(${target.dataset.src}) center/cover`;
        target.style.backgroundColor = 'unset';
        target.style.animation = 'unset';
        saveNewImageLoaded(target.dataset.src || '');
      };
      hdLoaderImg.onerror = () => {
        // eslint-disable-next-line no-param-reassign
        target.style.background = `url(${target.dataset.defaultImage}) center/cover`;
        target.style.backgroundColor = 'unset';
        target.style.animation = 'unset';
        saveNewImageLoaded(target.dataset.defaultImage || '');
      };
      observer.unobserve(target);
    }
  });
}

const LazyLoadingContext = React.createContext<{ observer: IntersectionObserver | undefined }>({
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  observer: undefined!!
});

const LazyLoadingContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const value = React.useMemo(() => {
    const config = {
      rootMargin: '100px',
      threshold: 0
    };
    return {
      observer: !isSSR() ? new IntersectionObserver(lazyLoadImage, config) : undefined
    };
  }, []);
  return <LazyLoadingContext.Provider value={value}>{children}</LazyLoadingContext.Provider>;
};

LazyLoadingContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

function useLazyLoading() {
  return useContext(LazyLoadingContext);
}

export { LazyLoadingContext, LazyLoadingContextProvider, lazyLoadImage, useLazyLoading };
